import type {
  CatalogFilter,
  CatalogItem,
  CatalogRepository,
  ExecutionContext,
  ProjectStockUsage,
  SaveCatalogItemCommand,
  WithdrawStockCommand,
} from "@dker/core";
import { DomainError } from "@dker/core";
import type { DbClientFactory } from "../ports.js";

/* eslint-disable @typescript-eslint/no-explicit-any */
type Row = Record<string, any>;

/**
 * Taille des lots d'insertion.
 *
 * PostgREST refuse les charges utiles trop volumineuses, et un import
 * catalogue tourne couramment à plusieurs centaines de lignes. On découpe donc
 * ici plutôt que dans le domaine : c'est une contrainte de transport, pas une
 * règle métier.
 */
const CHUNK = 40;

function toItem(row: Row): CatalogItem {
  return {
    id: row.id,
    category: row.category,
    subcategory: row.subcategory ?? null,
    name: row.name,
    description: row.description ?? null,
    reference: row.reference ?? null,
    unit: row.unit,
    unitCost: Number(row.unit_cost ?? 0),
    unitPrice: row.unit_price == null ? null : Number(row.unit_price),
    marginPercent: row.margin_percent == null ? null : Number(row.margin_percent),
    tvaRate: Number(row.tva_rate ?? 20),
    supplierId: row.supplier_id ?? null,
    isActive: row.is_active !== false,
    stockQuantity: row.stock_quantity == null ? null : Number(row.stock_quantity),
    reorderThreshold: row.reorder_threshold == null ? null : Number(row.reorder_threshold),
  };
}

function toColumns(command: SaveCatalogItemCommand): Row {
  return {
    category: command.category,
    subcategory: command.subcategory,
    name: command.name,
    unit: command.unit,
    unit_cost: command.unitCost,
    unit_price: command.unitPrice,
    margin_percent: command.marginPercent,
    tva_rate: command.tvaRate,
    supplier_id: command.supplierId,
    stock_quantity: command.stockQuantity,
    reorder_threshold: command.reorderThreshold,
  };
}

function fail(error: { message: string; code?: string }): never {
  throw new DomainError("conflict", `Opération refusée par la base : ${error.message}`, {
    pgCode: error.code,
  });
}

export function createSupabaseCatalogRepository(
  clientFor: DbClientFactory,
): CatalogRepository {
  return {
    async list(ctx: ExecutionContext, filter?: CatalogFilter): Promise<CatalogItem[]> {
      let query = clientFor(ctx)
        .from("catalog_items")
        .select("*")
        .is("deleted_at", null)
        .eq("is_active", true)
        .order("category", { ascending: true });

      if (filter?.category) query = query.eq("category", filter.category);
      if (filter?.search) {
        const needle = `%${filter.search}%`;
        query = query.or(`name.ilike.${needle},reference.ilike.${needle}`);
      }

      const { data, error } = await query;
      if (error) fail(error);
      return (data ?? []).map(toItem);
    },

    async save(ctx: ExecutionContext, command: SaveCatalogItemCommand): Promise<CatalogItem> {
      const db = clientFor(ctx);
      const columns = toColumns(command);

      const { data, error } = command.itemId
        ? await db
            .from("catalog_items")
            .update(columns)
            .eq("id", command.itemId)
            .select()
            .maybeSingle()
        : await db.from("catalog_items").insert(columns).select().single();

      if (error) fail(error);
      if (!data) {
        throw new DomainError("not_found", "Cet article n'existe pas.", {
          itemId: command.itemId,
        });
      }
      return toItem(data);
    },

    async saveMany(
      ctx: ExecutionContext,
      commands: SaveCatalogItemCommand[],
    ): Promise<number> {
      const db = clientFor(ctx);
      let inserted = 0;

      for (let i = 0; i < commands.length; i += CHUNK) {
        const slice = commands.slice(i, i + CHUNK).map(toColumns);
        const { error } = await db.from("catalog_items").insert(slice);
        if (error) {
          // On dit combien de lignes sont déjà passées : sans ça, l'utilisateur
          // ne sait pas s'il doit réimporter tout le fichier ou seulement la
          // fin, et il crée des doublons.
          throw new DomainError(
            "conflict",
            `Import interrompu après ${inserted} article(s) : ${error.message}. ` +
              `Les lignes déjà importées ont été conservées.`,
            { imported: inserted, pgCode: error.code },
          );
        }
        inserted += slice.length;
      }

      return inserted;
    },

    /**
     * Sortir du stock sur un chantier.
     *
     * Un seul appel : la fonction `withdraw_stock` écrit le mouvement, le
     * solde et la dépense dans la même transaction. Les recomposer ici
     * laisserait la porte à un solde décrémenté sans dépense — donc du
     * matériel disparu sans être imputé à personne.
     *
     * L'article est relu après coup pour que l'écran voie son nouveau solde
     * sans redemander ; la fonction ne rend que l'identifiant du mouvement,
     * parce que c'est lui qui compte pour la base.
     */
    async withdrawStock(
      ctx: ExecutionContext,
      command: WithdrawStockCommand,
    ): Promise<CatalogItem> {
      const db = clientFor(ctx);

      const { error } = await db.rpc("withdraw_stock", {
        p_item_id: command.itemId,
        p_project_id: command.projectId,
        p_quantity: command.quantity,
        p_note: command.note,
      });
      if (error) fail(error);

      const { data, error: relecture } = await db
        .from("catalog_items")
        .select("*")
        .eq("id", command.itemId)
        .maybeSingle();
      if (relecture) fail(relecture);
      if (!data) {
        throw new DomainError("not_found", "Cet article n'existe plus.", {
          itemId: command.itemId,
        });
      }
      return toItem(data);
    },

    async listProjectStockUsage(
      ctx: ExecutionContext,
      projectId: string,
    ): Promise<ProjectStockUsage[]> {
      const { data, error } = await clientFor(ctx)
        .from("project_stock_usage")
        .select("*")
        .eq("project_id", projectId)
        .order("created_at", { ascending: false });

      if (error) fail(error);
      return (data ?? []).map((row: Row) => ({
        id: row.id,
        projectId: row.project_id,
        catalogItemId: row.catalog_item_id,
        itemName: row.item_name,
        itemUnit: row.item_unit,
        quantity: Number(row.quantity ?? 0),
        unitCost: row.unit_cost == null ? null : Number(row.unit_cost),
        costHt: Number(row.cost_ht ?? 0),
        note: row.note ?? null,
        createdAt: row.created_at,
      }));
    },
  };
}
